import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import YouTube from "react-youtube";
import "../styles/Academy.css";

const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

const Academy = () => {
  const [videos, setVideos] = useState([]);
  const [articles, setArticles] = useState([]);
  const [secondVideos, setSecondVideos] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [minimizedVideo, setMinimizedVideo] = useState(null);
  const navigate = useNavigate();

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const carouselRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchVideosFromCMS = async () => {
      try {

        const cmsResponse = await fetch(
          `https://www.cmsnovatide.com/dashboard-content.json`,
        );

        console.log("cmsResponse-->",cmsResponse);
        const cmsData = await cmsResponse.json();

        const nexaVistaEntry = cmsData?.data?.find(
          (entry) => entry.title === "NexaVista YouTube Videos"
        );

        if (!nexaVistaEntry) {
          console.warn(
            'Could not find the entry with title "NexaVista YouTube Videos"'
          );
          return;
        }

        // Extract videos from each section
        const videoUrls = extractUrls(nexaVistaEntry.videosList);
        const articleUrls = extractUrls(nexaVistaEntry.articlesList);
        const secondVideoUrls = extractUrls(nexaVistaEntry.secondVideosList);

        // Fetch details from YouTube API for all lists
        const allUrls = [...videoUrls, ...articleUrls, ...secondVideoUrls];
        if (allUrls.length === 0) return;

        const videoData = await fetchVideoDetails(allUrls);

        // Map the response back into separate lists
        setVideos(mapVideos(videoUrls, videoData));
        setArticles(mapVideos(articleUrls, videoData));
        setSecondVideos(mapVideos(secondVideoUrls, videoData));
      } catch (error) {
        console.error("Error fetching from CMS or YouTube:", error);
      }
    };

    fetchVideosFromCMS();
  }, []);

  const extractUrls = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((video) => video.url);
  };

  const fetchVideoDetails = async (urls) => {
    const videoIds = urls
      .map((url) =>
        url.includes("watch?v=") ? url.split("watch?v=")[1].split("&")[0] : url
      )
      .join(",");

    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?key=${API_KEY}&part=snippet,contentDetails&id=${videoIds}`
    );
    const data = await response.json();
    return data.items || [];
  };

  const mapVideos = (urls, videoData) => {
    const lookup = {};
    videoData.forEach((item) => {
      lookup[item.id] = {
        title: item.snippet.title,
        thumbnail: item.snippet.thumbnails.high.url,
        duration: parseDuration(item.contentDetails.duration),
      };
    });

    return urls
      .map((originalUrl) => {
        const shortId = originalUrl.includes("watch?v=")
          ? originalUrl.split("watch?v=")[1].split("&")[0]
          : originalUrl;

        const snippet = lookup[shortId];
        if (!snippet) return null;

        return {
          id: originalUrl,
          title: snippet.title,
          thumbnail: snippet.thumbnail,
          duration: snippet.duration,
        };
      })
      .filter(Boolean);
  };

  const mapArticles = (articlesList, videoData) => {
    // We'll do an optional step: if the article's URL is YouTube, let's fetch its snippet
    const lookup = {};
    videoData.forEach((item) => {
      lookup[item.id] = {
        title: item.snippet.title,
        thumbnail: item.snippet.thumbnails.high.url,
        duration: parseDuration(item.contentDetails.duration),
      };
    });

    return articlesList.map((article) => {
      // article.url might be YouTube; let's get the ID
      let shortId = null;
      if (article.url.includes("watch?v=")) {
        shortId = article.url.split("watch?v=")[1].split("&")[0];
      }

      // If found in lookup, get that snippet
      const snippet = shortId ? lookup[shortId] : null;

      return {
        id: article.url, // or a unique ID from your CMS
        url: article.url,
        mainparagraph: article.mainparagraph || "",
        title: article.title || (snippet?.title ?? "No Title"),
        thumbnail: snippet?.thumbnail ?? "", // fallback if no snippet
        duration: snippet?.duration ?? "",
      };
    });
  };

  // Convert ISO duration (PT4M3S) to readable minutes
  const parseDuration = (isoDuration) => {
    const match = isoDuration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);

    const hours = match[1] ? parseInt(match[1].replace("H", "")) : 0;
    const minutes = match[2] ? parseInt(match[2].replace("M", "")) : 0;
    const seconds = match[3] ? parseInt(match[3].replace("S", "")) : 0;

    const totalMinutes = Math.round(hours * 60 + minutes + seconds / 60); // Convert everything to minutes
    return `${totalMinutes} min`;
  };

  const handleVideoClick = (videoUrl) => {
    // Close the minimized video if one exists
    if (minimizedVideo) {
      handleCloseMiniPlayer();
    }

    let videoId = videoUrl.includes("watch?v=")
      ? videoUrl.split("watch?v=")[1].split("&")[0]
      : null;

    const urlParams = new URLSearchParams(
      videoUrl.substring(videoUrl.indexOf("?") + 1)
    );
    const playlistId = urlParams.get("list");

    if (playlistId && videoId) {
      navigate(`/watch/${videoId}?list=${playlistId}`);
    } else if (videoId) {
      navigate(`/watch/${videoId}`);
    } else {
      console.warn("Could not parse videoId from URL:", videoUrl);
    }
  };

  const handleArticleClick = (videoUrl) => {
    if (minimizedVideo) {
      handleCloseMiniPlayer();
    }

    let videoId = videoUrl.includes("watch?v=")
      ? videoUrl.split("watch?v=")[1].split("&")[0]
      : null;

    const urlParams = new URLSearchParams(
      videoUrl.substring(videoUrl.indexOf("?") + 1)
    );
    const playlistId = urlParams.get("list");

    if (playlistId && videoId) {
      navigate(`/read/${videoId}?list=${playlistId}`);
    } else if (videoId) {
      navigate(`/read/${videoId}`);
    } else {
      console.warn("Could not parse videoId from URL:", videoUrl);
    }
  };

  useEffect(() => {
    const storedVideo = localStorage.getItem("minimizedVideo");
    if (storedVideo) {
      setMinimizedVideo(JSON.parse(storedVideo));
    }
  }, []);

  useEffect(() => {
    if (searchTerm.length > 0) {
      const allContent = [...videos, ...articles, ...secondVideos];
      const results = allContent.filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredResults(results);
    } else {
      setFilteredResults([]);
    }
  }, [searchTerm, videos, articles, secondVideos]);

  const handleCloseMiniPlayer = () => {
    localStorage.removeItem("minimizedVideo");
    setMinimizedVideo(null);
  };

  const handleSeeAllClick = () => {
    navigate("/academy/all", { state: { videos } }); // Pass videos as state
  };

  const handleSeeAllArticlesClick = () => {
    navigate("/academy/articles", {
      state: {
        articles,
      },
    }); // Pass videos as state
  };

  const handleSeeAllSecondClick = () => {
    navigate("/academy/second", { state: { secondVideos } }); // Pass videos as state
  };

  function startDrag(e) {
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  }

  function stopDrag() {
    setIsDragging(false);
  }

  function handleDrag(e) {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // multiplier for scroll speed
    carouselRef.current.scrollLeft = scrollLeft - walk;
  }

  const renderMobile = () => (
    <div className="academy-container">
      <h1 className="academy-title">Academy</h1>

      <div className="search-bar">
        <img src="static/images/searchSymbol.png" alt="Search" />
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {searchTerm.length > 0 ? (
        // Mostrar solo los resultados de búsqueda
        <div className="search-results-grid">
          {filteredResults.length > 0 ? (
            filteredResults.map((item) => (
              <div
                key={item.id}
                className="search-result-item"
                onClick={() => handleVideoClick(item.id)}
              >
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="search-thumbnail"
                />
                <h3 className="search-title">{item.title}</h3>
                <p className="search-duration">{item.duration}</p>
              </div>
            ))
          ) : (
            <p className="no-results">No results found.</p>
          )}
        </div>
      ) : (
        // Mostrar contenido normal si no hay búsqueda
        <>
          <div className="first-section-header">
            <h2 className="section-title">Title section 1</h2>
            <a
              href="/academy/all"
              className="see-all-link"
              onClick={handleSeeAllClick}
            >
              See All
              <img
                src="static/images/arrowRightSymbol.png"
                alt="See All"
                className="arrow-right-symbol"
              />
            </a>
          </div>

          {videos.length > 0 && (
            <>
              {/* Featured video */}
              <div
                className="featured-video"
                onClick={() => handleVideoClick(videos[0].id)}
              >
                <div className="thumbnail-container">
                  <img
                    src={videos[0].thumbnail}
                    alt={videos[0].title}
                    className="video-thumbnail"
                  />
                  <img
                    src="/static/images/playSymbol.png"
                    alt="Play"
                    className="play-icon"
                  />
                </div>
                <h3 className="video-title">{videos[0].title}</h3>
                <p className="video-duration">{videos[0].duration}</p>
              </div>
              <div className="video-grid">
                {videos.slice(0, 4).map((video) => (
                  <div
                    key={video.id}
                    className="grid-video"
                    onClick={() => handleVideoClick(video.id)}
                  >
                    <div className="thumbnail-container">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="video-thumbnail"
                      />
                      <img
                        src="/static/images/playSymbol.png"
                        alt="Play"
                        className="play-icon"
                      />
                    </div>
                    <h3 className="video-title">{video.title}</h3>
                    <p className="video-duration">{video.duration}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Mini Player */}
          {minimizedVideo && (
            <div className="mini-player">
              <YouTube
                videoId={minimizedVideo.videoId}
                opts={{ width: "160", height: "90" }}
              />
              <button className="close-mini" onClick={handleCloseMiniPlayer}>
                <img src="/static/images/closeSymbol.png" alt="Close" />
              </button>
            </div>
          )}

          <div className="submit-container">
            <p className="submit-label">
              Ready to see how your business is doing?
            </p>
            <button className="submit-green-button">Start Analysis</button>
          </div>

          {videos.length > 0 && (
            <>
              <div className="section-header">
                <h2 className="section-title">Title section 2</h2>
                <a
                  href="/academy/articles"
                  className="see-all-link"
                  onClick={handleSeeAllArticlesClick}
                >
                  See All
                  <img
                    src="static/images/arrowRightSymbol.png"
                    alt="See All"
                    className="arrow-right-symbol"
                  />
                </a>
              </div>

              <div className="vertical-list">
                {articles.slice(0, 4).map((article) => (
                  <div
                    key={article.id}
                    className="vertical-list-item"
                    onClick={() => handleArticleClick(article.id)}
                  >
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      className="vertical-thumbnail"
                    />
                    <h3 className="vertical-title">{article.title}</h3>
                  </div>
                ))}
              </div>
            </>
          )}

          {videos.length > 0 && (
            <>
              <div className="section-header">
                <h2 className="section-title">Title section 3</h2>
                <a
                  href="/academy/second"
                  className="see-all-link"
                  onClick={handleSeeAllSecondClick}
                >
                  See All
                  <img
                    src="static/images/arrowRightSymbol.png"
                    alt="See All"
                    className="arrow-right-symbol"
                  />
                </a>
              </div>

              <div className="carousel-container">
                {secondVideos.slice(0, 4).map((video) => (
                  <div
                    key={video.id}
                    className="carousel-item"
                    onClick={() => handleVideoClick(video.id)}
                  >
                    <div className="carousel-thumb-container">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="carousel-thumbnail"
                      />
                      <img
                        src="/static/images/playSymbol.png"
                        alt="Play"
                        className="carousel-play-icon"
                      />
                    </div>
                    <h3 className="carousel-video-title">{video.title}</h3>
                    <p className="carousel-video-duration">{video.duration}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="submit-container">
            <p className="submit-label-strong">
              How well is your business performing?
            </p>
            <p className="submit-label">Find out now for free.</p>
            <button className="submit-green-button">Evaluate Now</button>
          </div>
        </>
      )}
    </div>
  );

  const renderDesktop = () => (
    <div className="academy-container-desktop">
      <div className="desktop-header-row">
        <h1 className="academy-title-desktop">Academy</h1>
        <div className="search-bar-desktop">
          <img src="static/images/searchSymbol.png" alt="Search" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {searchTerm.length > 0 ? (
        <div className="search-results-grid-desktop">
          {filteredResults.length > 0 ? (
            filteredResults.map((item) => (
              <div
                key={item.id}
                className="search-result-item-desktop"
                onClick={() => handleVideoClick(item.id)}
              >
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="search-thumbnail-desktop"
                />
                <div className="search-text-container-desktop">
                  <h3 className="search-title-desktop">{item.title}</h3>
                  <p className="search-duration-desktop">{item.duration}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="no-results-desktop">No results found.</p>
          )}
        </div>
      ) : (
        // Mostrar contenido normal si no hay búsqueda
        <>
          <div className="first-section-header-desktop">
            <h2 className="section-title-desktop">Title section 1</h2>
            <a
              href="/academy/all"
              className="see-all-link-desktop"
              onClick={handleSeeAllClick}
            >
              See All
              <img
                src="static/images/arrowRightSymbol.png"
                alt="See All"
                className="arrow-right-symbol"
              />
            </a>
          </div>

          {videos.length > 0 && (
            <div className="title-section-1-desktop">
              {/* Left: Big Featured Video */}
              <div
                className="featured-video-desktop"
                onClick={() => handleVideoClick(videos[0].id)}
              >
                <div className="thumbnail-container-desktop">
                  <img
                    src={videos[0].thumbnail}
                    alt={videos[0].title}
                    className="video-thumbnail-desktop"
                  />
                  <img
                    src="/static/images/playSymbol.png"
                    alt="Play"
                    className="play-icon-desktop"
                  />
                </div>
                <h3 className="video-title-desktop">{videos[0].title}</h3>
                <p className="video-duration-desktop">{videos[0].duration}</p>
              </div>

              {/* Right: Grid of smaller videos */}
              <div className="video-grid-desktop">
                {videos.slice(1, 5).map((video) => (
                  <div
                    key={video.id}
                    className="grid-video-desktop"
                    onClick={() => handleVideoClick(video.id)}
                  >
                    <div className="thumbnail-container-desktop">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="video-thumbnail-desktop"
                      />
                      <img
                        src="/static/images/playSymbol.png"
                        alt="Play"
                        className="play-icon-desktop"
                      />
                    </div>
                    <h4 className="video-title-desktop">{video.title}</h4>
                    <p className="video-duration-desktop">{video.duration}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mini Player */}
          {minimizedVideo && (
            <div className="mini-player">
              <button
                className="close-mini-desktop"
                onClick={handleCloseMiniPlayer}
              >
                <img src="/static/images/closeSymbol.png" alt="Close" />
              </button>
              <YouTube
                videoId={minimizedVideo.videoId}
                opts={{ width: "250", height: "140" }}
              />
            </div>
          )}

          <div className="submit-container-desktop">
            <p className="submit-label-desktop">
              Ready to see how your
              <br /> business is doing?
            </p>
            <button className="submit-green-button-desktop">
              Start Analysis
            </button>
          </div>

          {videos.length > 0 && (
            <>
              <div className="section-header-desktop">
                <h2 className="section-title-desktop">Title section 2</h2>
                <a
                  href="/academy/articles"
                  className="see-all-link-desktop"
                  onClick={handleSeeAllArticlesClick}
                >
                  See All
                  <img
                    src="static/images/arrowRightSymbol.png"
                    alt="See All"
                    className="arrow-right-symbol"
                  />
                </a>
              </div>

              <div className="list-grid-desktop">
                {articles.slice(0, 4).map((article) => (
                  <div
                    key={article.id}
                    className="grid-item-desktop"
                    onClick={() => handleArticleClick(article.id)}
                  >
                    <img
                      src={article.thumbnail}
                      alt={article.title}
                      className="thumbnail-desktop"
                    />
                    <h3 className="article-title-desktop">{article.title}</h3>
                  </div>
                ))}
              </div>
            </>
          )}

          {videos.length > 0 && (
            <>
              <div className="section-header-desktop">
                <h2 className="section-title-desktop">Title section 3</h2>
                <a
                  href="/academy/second"
                  className="see-all-link-desktop"
                  onClick={handleSeeAllSecondClick}
                >
                  See All
                  <img
                    src="static/images/arrowRightSymbol.png"
                    alt="See All"
                    className="arrow-right-symbol"
                  />
                </a>
              </div>

              <div
                className="carousel-container-desktop"
                ref={carouselRef}
                onMouseDown={startDrag}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
                onMouseMove={handleDrag}
              >
                {secondVideos.slice(0, 4).map((video) => (
                  <div
                    key={video.id}
                    className="carousel-item-desktop"
                    onClick={() => handleVideoClick(video.id)}
                  >
                    <div className="carousel-thumb-container-desktop">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="carousel-thumbnail-desktop"
                      />
                      <img
                        src="/static/images/playSymbol.png"
                        alt="Play"
                        className="carousel-play-icon-desktop"
                      />
                    </div>
                    <h3 className="carousel-video-title-desktop">
                      {video.title}
                    </h3>
                    <p className="carousel-video-duration-desktop">
                      {video.duration}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="second-submit-container-desktop">
            <div className="text-block-desktop">
              <p className="submit-label-desktop">
                How well is your
                <br /> business performing?
              </p>
              <p className="submit-label-small-desktop">
                Find out now for free.
              </p>
            </div>
            <button className="submit-green-button-desktop">
              Evaluate Now
            </button>
          </div>
        </>
      )}
    </div>
  );

  return isMobile ? renderMobile() : renderDesktop();
};

export default Academy;